import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { clientUid, withClientUids } from "@/lib/clientUid";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import {
  Plus,
  Trash2,
  Save,
  X,
  Building2,
  Globe,
  ShieldCheck,
  Mail,
  FileText,
  ExternalLink,
  Boxes,
  Upload,
  Image as ImageIcon,
  Star,
  Pencil,
  Check,
  Search,
  Wand2,
  AlertTriangle,
} from "lucide-react";

const NEW_CATEGORIES = [
  "Compounding Pharmacy",
  "Clinical Laboratory",
  "Consumer Engagement",
  "Home Sleep Testing",
  "CPAP/DME",
  "Remote Monitoring",
  "Coaching Services",
  "Wellness Solutions",
  "Membership Solutions",
];
const STATUSES = ["active", "draft", "archived"];
const DOC_KINDS = ["compliance", "msa", "spec_sheet", "price_list", "training", "other"];
const PROGRAM_BUCKETS = ["sleep", "metabolic", "hormone", "diagnostics", "wellness"];
const BUCKET_STYLES = {
  sleep: "bg-indigo-50 text-indigo-700 border-indigo-200",
  metabolic: "bg-emerald-50 text-emerald-700 border-emerald-200",
  hormone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  diagnostics: "bg-sky-50 text-sky-700 border-sky-200",
  wellness: "bg-amber-50 text-amber-700 border-amber-200",
};
const EMPTY_VENDOR = {
  name: "",
  categories: [],
  description: "",
  website: "",
  contact_email: "",
  contact_name: "",
  pricing_notes: "",
  regions: "",
  compliance_attested: false,
  services: [],
  documents: [],
  status: "active",
};

export default function AdminVendorDirectory() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_VENDOR);
  const [categoriesList, setCategoriesList] = useState(NEW_CATEGORIES);
  const [uploading, setUploading] = useState(false);
  const [bookmarkStats, setBookmarkStats] = useState([]); // [{vendor_id,count,name,...}]
  const fileRef = useRef(null);
  const formRef = useRef(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBucket, setFilterBucket] = useState("all");
  const [search, setSearch] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  // Honor a `?bucket=wellness` (or `_none`) query string so the Command-Center
  // Vendor-Coverage widget can deep-link admins straight into a filtered view.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const b = searchParams.get("bucket");
    if (!b) return;
    const valid = ["all", "sleep", "metabolic", "hormone", "diagnostics", "wellness", "_none"];
    if (valid.includes(b)) setFilterBucket(b);
    // run-once on mount; subsequent user clicks shouldn't be overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anchor the editor form near the top of the viewport (rather than the
  // browser top) so opening Edit on a card lower in the list always lands
  // the form in plain view, not behind the sticky page header.
  const focusForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const load = async () => {
    try {
      const r = await api.get("/vendors");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const loadStats = async () => {
    try {
      const r = await api.get("/vendors/bookmark-stats");
      setBookmarkStats(r.data || []);
    } catch (e) {
      // Silent — non-blocking signal.
    }
  };
  useEffect(() => {
    load();
    loadStats();
    api
      .get("/taxonomy/vendor-categories")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setCategoriesList(r.data);
      })
      .catch(() => {});
  }, []);

  const bookmarkCountFor = useMemo(() => {
    const m = new Map();
    for (const s of bookmarkStats) m.set(s.vendor_id, s.count);
    return (vid) => m.get(vid) || 0;
  }, [bookmarkStats]);

  const topBookmarks = bookmarkStats.slice(0, 5);

  // Derived metrics for the new toolbar — KPIs across the full directory plus
  // per-category / per-status counts so the pills double as live filters AND
  // a quick rollup of where the network is concentrated.
  const kpis = useMemo(() => {
    const k = { total: items.length, active: 0, draft: 0, archived: 0, compliance: 0 };
    for (const v of items) {
      if (v.status === "active") k.active += 1;
      else if (v.status === "draft") k.draft += 1;
      else if (v.status === "archived") k.archived += 1;
      if (v.compliance_attested) k.compliance += 1;
    }
    return k;
  }, [items]);

  const categoryCounts = useMemo(() => {
    const c = { all: items.length };
    for (const v of items) {
      const list = (v.categories && v.categories.length > 0) ? v.categories : (v.category ? [v.category] : ["—"]);
      for (const k of list) c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [items]);

  const statusCounts = useMemo(() => {
    const c = { all: items.length };
    for (const v of items) {
      const k = v.status || "draft";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [items]);

  // Counts per derived `program_bucket` so admins can see at a glance how
  // many vendors map into each marketing bucket — and spot data-quality
  // issues (vendors that ended up in the "no bucket" tile usually have a
  // miscategorized or empty `category` field).
  const bucketCounts = useMemo(() => {
    const c = { all: items.length, _none: 0 };
    for (const v of items) {
      const b = v.program_bucket;
      if (b) c[b] = (c[b] || 0) + 1;
      else c._none += 1;
    }
    return c;
  }, [items]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items
      .filter((v) => {
        if (filterCategory === "all") return true;
        const list = (v.categories && v.categories.length > 0) ? v.categories : (v.category ? [v.category] : []);
        return list.includes(filterCategory);
      })
      .filter((v) => {
        if (filterBucket === "all") return true;
        if (filterBucket === "_none") return !v.program_bucket;
        return v.program_bucket === filterBucket;
      })
      .filter((v) => filterStatus === "all" || v.status === filterStatus)
      .filter((v) => !needle || (v.name || "").toLowerCase().includes(needle));
  }, [items, filterCategory, filterStatus, filterBucket, search]);
  const visibleCount = filteredItems.length;

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await api.post("/vendors/backfill-buckets");
      const { scanned, updated, unbucketed_sample = [] } = r.data || {};
      if (updated > 0) {
        toast.success(`Backfilled ${updated} of ${scanned} vendors.`);
        await load();
      } else if (unbucketed_sample.length > 0) {
        toast.warning(
          `Already in sync (${scanned} vendors). ${unbucketed_sample.length} categories couldn't be bucketed: ${unbucketed_sample.slice(0, 3).join(", ")}${unbucketed_sample.length > 3 ? "…" : ""}`,
        );
      } else {
        toast.success(`Already in sync — all ${scanned} vendors bucketed.`);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Backfill failed"));
    } finally {
      setBackfilling(false);
    }
  };

  const onLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo too large (2 MB max).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/vendors/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: r.data?.url || "" }));
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const startNew = (cat) => {
    setEditing("new");
    setForm({ ...EMPTY_VENDOR, categories: cat ? [cat] : [] });
    focusForm();
  };
  const startEdit = (v) => {
    setEditing(v.id);
    const inboundCats = Array.isArray(v.categories) && v.categories.length > 0
      ? v.categories
      : (v.category ? [v.category] : []);
    setForm({
      ...EMPTY_VENDOR,
      ...v,
      categories: inboundCats,
      services: withClientUids(v.services || [], "svc"),
      documents: withClientUids(v.documents || [], "doc"),
      contact_email: v.contact_email || "",
      website: v.website || "",
    });
    focusForm();
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_VENDOR);
  };

  const toggleCategory = (cat) => {
    setForm((f) => {
      const current = Array.isArray(f.categories) ? f.categories : [];
      return current.includes(cat)
        ? { ...f, categories: current.filter((c) => c !== cat) }
        : { ...f, categories: [...current, cat] };
    });
  };

  const sanitizePayload = (f) => {
    const clean = { ...f };
    if (!clean.contact_email) delete clean.contact_email;
    if (!clean.website) delete clean.website;
    // Iter 51: categories is authoritative. Drop legacy singular `category`
    // and `subcategory` so the backend's _normalize_categories sets the
    // legacy mirror from categories[0].
    clean.categories = Array.isArray(clean.categories) ? clean.categories.filter(Boolean) : [];
    delete clean.category;
    delete clean.subcategory;
    clean.services = (clean.services || []).map((s) => ({
      name: s.name,
      description: s.description || null,
      wholesale_cost: s.wholesale_cost === "" || s.wholesale_cost == null ? null : Number(s.wholesale_cost),
      suggested_retail_min:
        s.suggested_retail_min === "" || s.suggested_retail_min == null ? null : Number(s.suggested_retail_min),
      suggested_retail_max:
        s.suggested_retail_max === "" || s.suggested_retail_max == null ? null : Number(s.suggested_retail_max),
    }));
    clean.documents = (clean.documents || []).map((d) => ({
      label: d.label,
      url: d.url,
      kind: d.kind || "other",
      filename: d.filename || null,
      size: d.size == null || d.size === "" ? null : Number(d.size),
      content_type: d.content_type || null,
    }));
    return clean;
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Vendor name is required.");
      return;
    }
    if (!form.categories || form.categories.length === 0) {
      toast.error("Pick at least one category.");
      return;
    }
    try {
      const payload = sanitizePayload(form);
      if (editing === "new") {
        await api.post("/vendors", payload);
        toast.success("Vendor added. Updates are live across clinic portals + website.");
      } else {
        await api.patch(`/vendors/${editing}`, payload);
        toast.success("Vendor updated. Changes deploy immediately.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor from the directory?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Service editor helpers
  const addService = () =>
    setForm((f) => ({
      ...f,
      services: [...(f.services || []), { _uid: clientUid("svc"), name: "", description: "", wholesale_cost: "", suggested_retail_min: "", suggested_retail_max: "" }],
    }));
  const updService = (i, patch) =>
    setForm((f) => ({ ...f, services: f.services.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const removeService = (i) => setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));

  // Document editor helpers
  const addDoc = () =>
    setForm((f) => ({ ...f, documents: [...(f.documents || []), { _uid: clientUid("doc"), label: "", url: "", kind: "other" }] }));
  const updDoc = (i, patch) =>
    setForm((f) => ({ ...f, documents: f.documents.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) }));
  const removeDoc = (i) => setForm((f) => ({ ...f, documents: f.documents.filter((_, idx) => idx !== i) }));

  const [docUploading, setDocUploading] = useState({}); // {idx: true}
  const docFileRefs = useRef({});
  const onDocFileSelect = async (i, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Document too large (10 MB max).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setDocUploading((s) => ({ ...s, [i]: true }));
    try {
      const r = await api.post("/vendors/upload-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updDoc(i, {
        url: r.data?.url || "",
        filename: r.data?.filename || file.name,
        size: r.data?.size || file.size,
        content_type: r.data?.content_type || file.type,
        label: form.documents?.[i]?.label || (file.name.replace(/\.[^.]+$/, "") || "Document"),
      });
      toast.success("Document uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDocUploading((s) => ({ ...s, [i]: false }));
    }
  };

  const formatBytes = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Network"
      title="Vendors"
      intro="Manage the curated vendor directory. Edits propagate to clinic portals and the public site immediately."
      actions={
        <button
          data-testid="vendor-dir-new-btn"
          onClick={() => startNew()}
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> New vendor
        </button>
      }
    >
      {editing && (
        <div ref={formRef} data-testid="vendor-dir-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-5 scroll-mt-24">
          <Field label="Vendor name" required>
            <input
              data-testid="vendor-dir-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field
            label="Categories"
            hint="Pick all that apply. Vendors can serve multiple categories."
          >
            <div
              className="flex flex-wrap gap-2"
              data-testid="vendor-dir-form-categories"
              role="group"
              aria-label="Vendor categories"
            >
              {categoriesList.map((c) => {
                const selected = Array.isArray(form.categories) && form.categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    data-testid={`vendor-dir-form-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-pressed={selected}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      selected
                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                    }`}
                  >
                    {selected && <Check className="w-3 h-3" />}
                    {c}
                  </button>
                );
              })}
            </div>
            {Array.isArray(form.categories) && form.categories.length === 0 && (
              <div className="text-[11px] text-amber-700 mt-2">
                At least one category is required.
              </div>
            )}
          </Field>
          <Field label="Description">
            <textarea
              rows={3}
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Website">
              <input
                value={form.website || ""}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://"
                className={inputCls}
              />
            </Field>
            <Field label="Logo" hint="Upload PNG/JPG/WEBP/SVG up to 2 MB, or paste a URL.">
              <div className="flex items-center gap-3">
                {form.logo_url ? (
                  <img
                    src={resolveAssetUrl(form.logo_url)}
                    alt="Logo preview"
                    className="w-12 h-12 rounded-xl object-cover border border-slate-200 bg-white shrink-0"
                  />
                ) : (
                  <span className="w-12 h-12 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center shrink-0 text-slate-400">
                    <ImageIcon className="w-4 h-4" />
                  </span>
                )}
                <div className="flex-1 min-w-0 space-y-1.5">
                  <input
                    value={form.logo_url || ""}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    placeholder="https:// or upload"
                    className={inputCls}
                    data-testid="vendor-dir-form-logo-url"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="vendor-dir-form-logo-upload"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
                    >
                      <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload file"}
                    </button>
                    {form.logo_url && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, logo_url: "" })}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={onLogoSelect}
                    className="hidden"
                  />
                </div>
              </div>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Contact email">
              <input
                value={form.contact_email || ""}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Contact name">
              <input
                value={form.contact_name || ""}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Regions / states">
              <input
                value={form.regions || ""}
                onChange={(e) => setForm({ ...form, regions: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Pricing notes">
            <textarea
              rows={2}
              value={form.pricing_notes || ""}
              onChange={(e) => setForm({ ...form, pricing_notes: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!form.compliance_attested}
              onChange={(e) => setForm({ ...form, compliance_attested: e.target.checked })}
              data-testid="vendor-dir-form-compliance"
            />
            Compliance attested by vendor
          </label>

          {/* Services editor */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 inline-flex items-center gap-1.5">
                <Boxes className="w-3 h-3" /> Services & pricing
              </div>
              <button
                onClick={addService}
                data-testid="vendor-dir-add-service"
                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
              >
                + Add service
              </button>
            </div>
            <div className="space-y-3">
              {(form.services || []).map((s, i) => (
                <div key={s._uid || `svc-idx-${i}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input
                      value={s.name || ""}
                      onChange={(e) => updService(i, { name: e.target.value })}
                      placeholder="Service name"
                      className={inputCls}
                      data-testid={`vendor-dir-svc-name-${i}`}
                    />
                    <button
                      onClick={() => removeService(i)}
                      className="text-xs text-red-600 hover:text-red-800 self-end justify-self-end"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    value={s.description || ""}
                    onChange={(e) => updService(i, { description: e.target.value })}
                    placeholder="Description"
                    className={inputCls}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      value={s.wholesale_cost ?? ""}
                      onChange={(e) => updService(i, { wholesale_cost: e.target.value })}
                      placeholder="Wholesale $"
                      className={inputCls}
                    />
                    <input
                      type="number"
                      value={s.suggested_retail_min ?? ""}
                      onChange={(e) => updService(i, { suggested_retail_min: e.target.value })}
                      placeholder="Retail min $"
                      className={inputCls}
                    />
                    <input
                      type="number"
                      value={s.suggested_retail_max ?? ""}
                      onChange={(e) => updService(i, { suggested_retail_max: e.target.value })}
                      placeholder="Retail max $"
                      className={inputCls}
                    />
                  </div>
                </div>
              ))}
              {(form.services || []).length === 0 && (
                <div className="text-xs text-slate-500">No services attached yet.</div>
              )}
            </div>
          </div>

          {/* Documents editor */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 inline-flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Documents
                <span className="normal-case tracking-normal text-slate-400 font-normal ml-1">(PDF, DOC, XLS, PPT, CSV, TXT, image — 10 MB max)</span>
              </div>
              <button
                onClick={addDoc}
                data-testid="vendor-dir-add-doc"
                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
              >
                + Add document
              </button>
            </div>
            <div className="space-y-3">
              {(form.documents || []).map((d, i) => (
                <div
                  key={d._uid || `doc-idx-${i}`}
                  data-testid={`vendor-dir-doc-row-${i}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2"
                >
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input
                      value={d.label || ""}
                      onChange={(e) => updDoc(i, { label: e.target.value })}
                      placeholder="Label (e.g. CLIA Certificate)"
                      className={inputCls}
                      data-testid={`vendor-dir-doc-label-${i}`}
                    />
                    <select
                      value={d.kind || "other"}
                      onChange={(e) => updDoc(i, { kind: e.target.value })}
                      className={inputCls}
                      data-testid={`vendor-dir-doc-kind-${i}`}
                    >
                      {DOC_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => removeDoc(i)}
                        data-testid={`vendor-dir-doc-remove-${i}`}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                  <input
                    value={d.url || ""}
                    onChange={(e) => updDoc(i, { url: e.target.value })}
                    placeholder="Paste URL OR use Upload file →"
                    className={inputCls}
                    data-testid={`vendor-dir-doc-url-${i}`}
                  />
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => docFileRefs.current[i]?.click()}
                      disabled={!!docUploading[i]}
                      data-testid={`vendor-dir-doc-upload-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
                    >
                      <Upload className="w-3 h-3" /> {docUploading[i] ? "Uploading…" : d.url ? "Replace file" : "Upload file"}
                    </button>
                    <input
                      ref={(el) => (docFileRefs.current[i] = el)}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/csv,text/plain,image/*"
                      onChange={(e) => onDocFileSelect(i, e)}
                      className="hidden"
                    />
                    {d.filename && (
                      <span className="text-[11px] text-slate-600 inline-flex items-center gap-1">
                        <FileText className="w-3 h-3 text-slate-400" />
                        <span className="font-medium truncate max-w-[18ch]" title={d.filename}>
                          {d.filename}
                        </span>
                        {d.size ? <span className="text-slate-400">· {formatBytes(d.size)}</span> : null}
                      </span>
                    )}
                    {d.url && (
                      <a
                        href={resolveAssetUrl(d.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                      >
                        Preview <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {(form.documents || []).length === 0 && (
                <div className="text-xs text-slate-500">No documents attached yet.</div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="vendor-dir-form-save"
              onClick={save}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Save className="w-3.5 h-3.5" /> Save & deploy
            </button>
            <button
              onClick={cancel}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {topBookmarks.length > 0 && !editing && (
        <div
          data-testid="vendor-dir-top-bookmarks"
          className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
              Top saved vendors · clinic traction signal
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {topBookmarks.map((b) => (
              <span
                key={b.vendor_id}
                className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-full pl-3 pr-2 py-1 text-xs"
              >
                <span className="font-semibold text-slate-900 truncate max-w-[18ch]">{b.name}</span>
                <span className="text-slate-400 capitalize">· {b.category}</span>
                <span className="ml-1 inline-flex items-center justify-center bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[1.25rem] h-5 px-1.5">
                  {b.count}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* KPI strip — quick read on directory health */}
      <div
        className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4"
        data-testid="vendor-dir-kpis"
      >
        <KpiCard label="Total" value={kpis.total} testid="vendor-kpi-total" />
        <KpiCard label="Active" value={kpis.active} tone="emerald" testid="vendor-kpi-active" />
        <KpiCard label="Draft" value={kpis.draft} tone="amber" testid="vendor-kpi-draft" />
        <KpiCard label="Archived" value={kpis.archived} tone="slate" testid="vendor-kpi-archived" />
        <KpiCard
          label="Compliance attested"
          value={kpis.compliance}
          tone="blue"
          testid="vendor-kpi-compliance"
        />
      </div>

      {/* Toolbar — search + category pills + status pills */}
      <div
        className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 space-y-3"
        data-testid="vendor-dir-filters"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by vendor name…"
              data-testid="vendor-dir-search"
              className={`${inputCls} pl-9`}
            />
          </div>
          {filterCategory !== "all" && (
            <button
              type="button"
              onClick={() => startNew(filterCategory)}
              data-testid={`vendor-dir-add-in-${filterCategory}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-3 py-2 capitalize"
            >
              <Plus className="w-3 h-3" /> Add {filterCategory} vendor
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">
            Category
          </span>
          <button
            type="button"
            onClick={() => setFilterCategory("all")}
            data-testid="vendor-dir-cat-all"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filterCategory === "all"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            All · {categoryCounts.all}
          </button>
          {categoriesList.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilterCategory(c)}
              data-testid={`vendor-dir-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filterCategory === c
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {c} · {categoryCounts[c] || 0}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">
            Status
          </span>
          <button
            type="button"
            onClick={() => setFilterStatus("all")}
            data-testid="vendor-dir-status-all"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filterStatus === "all"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            All · {statusCounts.all}
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatus(s)}
              data-testid={`vendor-dir-status-${s}`}
              className={`text-xs capitalize px-3 py-1.5 rounded-full border transition-colors ${
                filterStatus === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s} · {statusCounts[s] || 0}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
            Showing <span className="font-bold text-slate-900">{visibleCount}</span> of{" "}
            <span className="font-bold text-slate-900">{kpis.total}</span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">
            Program bucket
          </span>
          <button
            type="button"
            onClick={() => setFilterBucket("all")}
            data-testid="vendor-dir-bucket-all"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filterBucket === "all"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            All · {bucketCounts.all}
          </button>
          {PROGRAM_BUCKETS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setFilterBucket(b)}
              data-testid={`vendor-dir-bucket-${b}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
                filterBucket === b
                  ? "bg-slate-900 text-white border-slate-900"
                  : `${BUCKET_STYLES[b]} hover:opacity-80`
              }`}
            >
              {b} · {bucketCounts[b] || 0}
            </button>
          ))}
          {bucketCounts._none > 0 && (
            <button
              type="button"
              onClick={() => setFilterBucket("_none")}
              data-testid="vendor-dir-bucket-none"
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                filterBucket === "_none"
                  ? "bg-rose-900 text-white border-rose-900"
                  : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
              }`}
              title="Vendors whose category doesn't map to any marketing bucket. These are invisible to the public /vendors page."
            >
              <AlertTriangle className="w-3 h-3" />
              No bucket · {bucketCounts._none}
            </button>
          )}
          <button
            type="button"
            onClick={runBackfill}
            disabled={backfilling}
            data-testid="vendor-dir-backfill-btn"
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-full px-3 py-1.5 disabled:opacity-60"
            title="Recompute & persist program_bucket on every vendor doc. Idempotent — safe to re-run."
          >
            <Wand2 className={`w-3 h-3 ${backfilling ? "animate-pulse" : ""}`} />
            {backfilling ? "Backfilling…" : "Backfill buckets"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4" data-testid="vendor-dir-list">
        {filteredItems.map((v) => (
          <div key={v.id} data-testid={`vendor-dir-card-${v.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                {v.logo_url ? (
                  <img src={resolveAssetUrl(v.logo_url)} alt={v.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-5 h-5 text-slate-700" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-display text-base font-semibold text-slate-900 truncate">{v.name}</div>
                  <StatusPill status={v.status} />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`vendor-card-categories-${v.id}`}>
                  {v.program_bucket ? (
                    <span
                      data-testid={`vendor-card-bucket-${v.id}`}
                      title="Derived program bucket — the public /vendors page counts this vendor in this tile."
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.1em] border capitalize ${BUCKET_STYLES[v.program_bucket] || "bg-slate-50 text-slate-700 border-slate-200"}`}
                    >
                      {v.program_bucket}
                    </span>
                  ) : (
                    <span
                      data-testid={`vendor-card-bucket-${v.id}`}
                      title="No marketing bucket — this vendor is invisible on the public /vendors page. Edit its category or click Backfill buckets."
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.1em] bg-rose-50 text-rose-700 border border-rose-200"
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      No bucket
                    </span>
                  )}
                  {((v.categories && v.categories.length > 0) ? v.categories : (v.category ? [v.category] : [])).map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.1em] bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      {c}
                    </span>
                  ))}
                  {v.subcategory && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-medium text-slate-700 bg-slate-100 border border-slate-200">
                      {v.subcategory}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-600 line-clamp-2">{v.description}</p>

            <div className="mt-3 space-y-1 text-xs text-slate-600">
              {v.website && (
                <div className="inline-flex items-center gap-1.5">
                  <Globe className="w-3 h-3 text-slate-400" />{" "}
                  <a href={v.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline truncate">
                    {v.website}
                  </a>
                </div>
              )}
              {v.contact_email && (
                <div className="inline-flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-slate-400" /> {v.contact_email}
                </div>
              )}
              {v.compliance_attested && (
                <div className="inline-flex items-center gap-1.5 text-emerald-700">
                  <ShieldCheck className="w-3 h-3" /> Compliance attested
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                <div className="uppercase tracking-[0.16em] font-bold text-slate-400">Services</div>
                <div className="font-display text-base font-bold text-slate-900 tabular">{(v.services || []).length}</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                <div className="uppercase tracking-[0.16em] font-bold text-slate-400">Documents</div>
                <div className="font-display text-base font-bold text-slate-900 tabular">{(v.documents || []).length}</div>
              </div>
              <div
                className={`rounded-lg border px-3 py-2 ${
                  bookmarkCountFor(v.id) > 0
                    ? "bg-amber-50 border-amber-200"
                    : "bg-slate-50 border-slate-100"
                }`}
                data-testid={`vendor-dir-card-bookmarks-${v.id}`}
              >
                <div className="uppercase tracking-[0.16em] font-bold text-slate-400 inline-flex items-center gap-1">
                  <Star className={`w-2.5 h-2.5 ${bookmarkCountFor(v.id) > 0 ? "text-amber-500 fill-amber-500" : ""}`} />
                  Saved by
                </div>
                <div className="font-display text-base font-bold text-slate-900 tabular">{bookmarkCountFor(v.id)}</div>
              </div>
            </div>

            {(v.documents || []).length > 0 && (
              <ul className="mt-3 space-y-1">
                {(v.documents || []).slice(0, 3).map((d, i) => (
                  <li key={d.url || `${d.label || "doc"}-${i}`} className="text-xs">
                    <a
                      href={resolveAssetUrl(d.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900"
                    >
                      <FileText className="w-3 h-3" /> {d.label} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
              <button
                onClick={() => startEdit(v)}
                data-testid={`vendor-dir-edit-${v.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-800 transition"
              >
                <Pencil className="w-3 h-3" /> Edit vendor
              </button>
              <button
                onClick={() => remove(v.id)}
                data-testid={`vendor-dir-delete-${v.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 px-3 py-1.5 text-xs font-semibold ml-auto"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="lg:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
            {items.length === 0 ? 'No vendors yet. Click "New vendor" to add one.' : "No vendors match the current filters."}
          </div>
        )}
      </div>
    </ToolPageShell>
  );
}

function KpiCard({ label, value, tone = "slate", testid }) {
  const TONE = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-3 ${TONE[tone] || TONE.slate}`}
    >
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">
        {label}
      </div>
      <div className="text-xl font-bold tabular-nums leading-tight mt-0.5">{value}</div>
    </div>
  );
}
